import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, TrendingDown, Zap, ArrowRightLeft, BarChart3 } from 'lucide-react';

const PAIRS = [
  { pair: 'BTC/USDT', color: '#F7931A' },
  { pair: 'ETH/USDT', color: '#627EEA' },
  { pair: 'SOL/USDT', color: '#9945FF' },
  { pair: 'BNB/USDT', color: '#F3BA2F' },
  { pair: 'XRP/USDT', color: '#00AAE4' },
  { pair: 'ADA/USDT', color: '#0033AD' },
  { pair: 'DOGE/USDT', color: '#C2A633' },
  { pair: 'AVAX/USDT', color: '#E84142' },
  { pair: 'DOT/USDT', color: '#E6007A' },
  { pair: 'LINK/USDT', color: '#2A5ADA' },
  { pair: 'LTC/USDT', color: '#BFBBBB' },
  { pair: 'TRX/USDT', color: '#EF0027' },
];

const TRADE_TYPES = ['Scalp', 'Swing', 'Position'];

const rand = (min, max) => Math.random() * (max - min) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const generateTrade = (id) => {
  const pairInfo = pick(PAIRS);
  const isWin = Math.random() < 0.65; // 65% win rate
  const isClosed = Math.random() < 0.7;
  const isRunning = !isClosed && Math.random() < 0.5;

  const pct = isWin ? rand(0.3, 4.8) : rand(0.2, 2.5);
  const baseAmt = rand(15, 200);
  const amt = isWin ? baseAmt * (pct / 100) : -(baseAmt * (pct / 100));

  let status, result;
  if (isClosed) {
    status = 'Closed';
    result = isWin ? 'Profit' : 'Loss';
  } else if (isRunning) {
    status = 'Running';
    result = null;
  } else {
    status = 'Opening';
    result = null;
  }

  const tradeType = pick(TRADE_TYPES);
  const descriptions = isClosed
    ? [
        `${pairInfo.pair} ${tradeType.toLowerCase()} closed ${isWin ? 'in profit' : 'at loss'}`,
        `${pairInfo.pair} ${tradeType.toLowerCase()} trade completed`,
        `${pairInfo.pair} ${isWin ? 'profit' : 'loss'} — ${tradeType.toLowerCase()} closed`,
      ]
    : isRunning
    ? [
        `${pairInfo.pair} ${tradeType.toLowerCase()} trade active`,
        `${pairInfo.pair} ${tradeType.toLowerCase()} running`,
      ]
    : [
        `${pairInfo.pair} ${tradeType.toLowerCase()} trade opened`,
        `Opening ${pairInfo.pair} ${tradeType.toLowerCase()} position`,
      ];

  return {
    id,
    pair: pairInfo.pair,
    color: pairInfo.color,
    type: tradeType,
    status,
    result,
    description: pick(descriptions),
    percentage: isClosed ? (isWin ? pct : -pct) : null,
    amount: isClosed ? amt : null,
    timestamp: new Date(),
  };
};

const formatTime = (d) =>
  d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

const StatusBadge = ({ status, result }) => {
  if (status === 'Running')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
        Running
      </span>
    );
  if (status === 'Opening')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">
        <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
        Opening
      </span>
    );
  if (result === 'Profit')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
        <TrendingUp className="w-3 h-3" /> Profit
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-red-500/10 text-red-400 border border-red-500/20">
      <TrendingDown className="w-3 h-3" /> Loss
    </span>
  );
};

const TradeRow = ({ trade }) => {
  const isProfit = trade.result === 'Profit';
  const isLoss = trade.result === 'Loss';

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -20, height: 0 }}
      animate={{ opacity: 1, x: 0, height: 'auto' }}
      exit={{ opacity: 0, x: 20, height: 0 }}
      transition={{ type: 'spring', stiffness: 400, damping: 30 }}
      className={`flex items-center gap-3 py-2.5 px-3 rounded-xl border transition-colors cursor-default
        ${isProfit ? 'border-emerald-500/10 bg-emerald-500/[0.02] hover:bg-emerald-500/[0.05]' : ''}
        ${isLoss ? 'border-red-500/10 bg-red-500/[0.02] hover:bg-red-500/[0.05]' : ''}
        ${!isProfit && !isLoss ? 'border-white/[0.04] bg-white/[0.01] hover:bg-white/[0.03]' : ''}
      `}
      data-testid="live-trade-row"
    >
      {/* Pair icon */}
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0"
        style={{ background: `${trade.color}18`, border: `1px solid ${trade.color}35` }}
      >
        {trade.pair.split('/')[0].slice(0, 2)}
      </div>

      {/* Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-white text-sm font-medium truncate">{trade.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={trade.status} result={trade.result} />
          <span className="text-slate-600 text-[10px]">{formatTime(trade.timestamp)}</span>
        </div>
      </div>

      {/* Result */}
      <div className="text-right flex-shrink-0">
        {trade.percentage !== null && (
          <>
            <p className={`text-sm font-bold tabular-nums ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
              {isProfit ? '+' : ''}{trade.percentage.toFixed(2)}%
            </p>
            <p className={`text-[11px] font-medium tabular-nums ${isProfit ? 'text-emerald-400/60' : 'text-red-400/60'}`}>
              {trade.amount >= 0 ? '+' : ''}${Math.abs(trade.amount).toFixed(2)}
            </p>
          </>
        )}
        {trade.status === 'Running' && (
          <p className="text-xs text-cyan-400 font-medium">Active</p>
        )}
        {trade.status === 'Opening' && (
          <p className="text-xs text-yellow-400/70 font-medium">Pending</p>
        )}
      </div>
    </motion.div>
  );
};

const LiveTradeActivity = () => {
  const [trades, setTrades] = useState([]);
  const idCounter = useRef(0);
  const intervalRef = useRef(null);

  const addTrade = useCallback(() => {
    idCounter.current += 1;
    const newTrade = generateTrade(idCounter.current);
    setTrades((prev) => [newTrade, ...prev].slice(0, 10));
  }, []);

  useEffect(() => {
    // Seed initial trades
    const initial = [];
    for (let i = 0; i < 6; i++) {
      idCounter.current += 1;
      const t = generateTrade(idCounter.current);
      t.timestamp = new Date(Date.now() - (6 - i) * 5000);
      initial.push(t);
    }
    setTrades(initial.reverse());

    // Add new trades at random intervals (4-9 seconds)
    const scheduleNext = () => {
      const delay = 4000 + Math.random() * 5000;
      intervalRef.current = setTimeout(() => {
        addTrade();
        scheduleNext();
      }, delay);
    };
    scheduleNext();

    return () => { if (intervalRef.current) clearTimeout(intervalRef.current); };
  }, [addTrade]);

  const winCount = trades.filter(t => t.result === 'Profit').length;
  const totalClosed = trades.filter(t => t.status === 'Closed').length;
  const winRate = totalClosed > 0 ? ((winCount / totalClosed) * 100).toFixed(0) : '--';

  return (
    <div data-testid="live-trade-activity">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <h2 className="text-lg font-bold text-white">Live Trade Activity</h2>
          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-[11px]">
            <BarChart3 className="w-3 h-3 text-slate-500" />
            <span className="text-slate-500">Win rate:</span>
            <span className="text-emerald-400 font-semibold">{winRate}%</span>
          </div>
        </div>
      </div>

      {/* Trade List */}
      <div className="space-y-1.5 max-h-[400px] overflow-y-auto scrollbar-hide">
        <AnimatePresence mode="popLayout">
          {trades.map((trade) => (
            <TradeRow key={trade.id} trade={trade} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default LiveTradeActivity;
